import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';
import { getTrialBrandLogo } from '../api/workspace';

/**
 * Reading a trial's brand mark out of a **private** bucket.
 *
 * There is no public URL to build, and the workspace used to build one anyway —
 * `https://storage.googleapis.com/<logo_gcs_path>` off the frozen snapshot, against a
 * bucket that answers 403 to everybody. The image failed, `HouseMark` fell back to its
 * glyph, and an employer-branded trial showed no logo with nothing to say why. The
 * server holds the only credential that can sign; `GET work-trials/{id}/brand-logo`
 * mints a V4 read URL good for fifteen minutes. Everything here follows from that one
 * fact: **a URL is a lease, not an address**, and any surface holding one has to be able
 * to ask for another.
 *
 * **Three things can hand a component a dead URL**, and each has its own answer here:
 *
 * - *Time.* A workspace left open past the lease — and this one is left open for hours,
 *   which is more than the admin shelf this reasoning was ported from ever was. The
 *   store re-mints at ten minutes rather than at fifteen, so a URL about to lapse is
 *   replaced before an `<img>` is pointed at it.
 * - *Return.* A tab woken from the background, or a page restored from bfcache — where
 *   the DOM comes back with the old `src` already in it and React re-renders nothing.
 *   `pageshow` and `visibilitychange` are the only notice of that, so both are listened
 *   for.
 * - *Anything else.* Clock skew, a restarted service, an object replaced under the URL.
 *   The image simply fails, and `onError` is the only signal — so a failed load re-mints
 *   once, and only once per URL, because a mark whose object is genuinely gone fails on
 *   every URL and retrying each failure is an unbounded loop.
 *
 * The cache is module-level rather than per component because the same trial's mark is on
 * screen twice — the rail head and the memo header both mount `HouseMark` — and because
 * the Appli-level Home draws the same mark on a door onto the same trial. Two or three
 * components, one lease, one refresh when it turns over.
 *
 * Keyed by **work trial id**, never by kit id. The server takes no kit id either: a
 * candidate reaches the mark of the kit their own trial froze, and of no other.
 */

/**
 * When a lease stops being trusted. The server signs for fifteen minutes and reports that
 * as `expires_in_minutes`; this sits well inside it, because the gap is the round trip
 * plus however long the image takes to arrive — a URL handed to an `<img>` at 14:59 is a
 * URL that expires mid-download.
 */
export const REFRESH_AFTER_MS = 10 * 60 * 1000;

/** `idle` before anything is asked, `none` for a kit that has no mark to show. */
const IDLE = { url: '', phase: 'idle', mintedAt: 0, attempt: 0 };

/** One entry per trial, replaced rather than mutated so identity is the change signal. */
const leases = new Map();
const inFlight = new Map();
const listeners = new Set();

const publish = (workTrialId, next) => {
    leases.set(workTrialId, next);
    listeners.forEach((listener) => listener());
};

const subscribe = (listener) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
};

const read = (workTrialId) => leases.get(workTrialId) || IDLE;

const stale = (state) => state.phase !== 'ready' || Date.now() - state.mintedAt > REFRESH_AFTER_MS;

const mint = (workTrialId) => {
    const running = inFlight.get(workTrialId);
    if (running) return running;

    const current = read(workTrialId);
    // Loading, but still holding the last good URL: a re-mint must not blank a mark that
    // is on screen and still working, or every refresh flashes the glyph underneath it.
    publish(workTrialId, { ...current, phase: current.url ? current.phase : 'loading' });

    const request = getTrialBrandLogo(workTrialId)
        .then(({ url }) => {
            publish(workTrialId, {
                ...read(workTrialId),
                url,
                phase: url ? 'ready' : 'none',
                mintedAt: Date.now(),
            });
        })
        .catch(() => {
            // No message is kept and none is shown. A mark that cannot be leased is a
            // mark the glyph stands in for, silently — this is livery, not an error the
            // candidate can act on, and a workspace is not the place to say so.
            publish(workTrialId, { ...read(workTrialId), url: '', phase: 'error', mintedAt: Date.now() });
        })
        .finally(() => {
            inFlight.delete(workTrialId);
        });

    inFlight.set(workTrialId, request);
    return request;
};

/** Drop a trial's lease so the next view mints a new one. Exported for tests. */
export const forgetTrialBrandLogo = (workTrialId) => {
    if (!workTrialId) return;
    leases.delete(workTrialId);
    inFlight.delete(workTrialId);
    listeners.forEach((listener) => listener());
};

/**
 * The trial's current logo URL, kept alive for as long as it is on screen.
 *
 * `hasLogo` is the frozen snapshot's own `logo_gcs_path` — the one bit that path still
 * carries now that no client can resolve it — and it is what stops this from asking: a
 * kit that never uploaded a mark would otherwise spend a 404 per mount to be told so.
 *
 * @param {string} workTrialId
 * @param {boolean} hasLogo - Whether the trial's snapshot names a logo at all
 * @returns {{url: string, phase: string, attempt: number, reload: Function, onImageError: Function}}
 */
export const useTrialBrandLogo = (workTrialId, hasLogo) => {
    const active = Boolean(workTrialId) && Boolean(hasLogo);

    const snapshot = useSyncExternalStore(
        subscribe,
        useCallback(() => (active ? read(workTrialId) : IDLE), [workTrialId, active])
    );

    // Which URL this component has already seen fail, so one re-mint is tried and no more.
    const failed = useRef('');

    const reload = useCallback(() => {
        if (!active) return;
        mint(workTrialId);
    }, [workTrialId, active]);

    useEffect(() => {
        if (!active) return;
        failed.current = '';
        if (stale(read(workTrialId))) mint(workTrialId);
    }, [workTrialId, active]);

    useEffect(() => {
        if (!active) return undefined;

        // A tab woken up, and a page restored from bfcache — where the `<img>` comes back
        // carrying a `src` that expired while the page was in the cache and React has
        // re-rendered nothing at all.
        const wake = () => {
            if (document.visibilityState === 'hidden') return;
            if (stale(read(workTrialId))) mint(workTrialId);
        };
        const restored = (event) => {
            if (event.persisted) mint(workTrialId);
            else wake();
        };

        document.addEventListener('visibilitychange', wake);
        window.addEventListener('pageshow', restored);
        return () => {
            document.removeEventListener('visibilitychange', wake);
            window.removeEventListener('pageshow', restored);
        };
    }, [workTrialId, active]);

    /** The `<img>` could not load this URL. Re-mint once per URL, then wear the glyph. */
    const onImageError = useCallback(() => {
        if (!active) return;
        const current = read(workTrialId);
        if (!current.url || failed.current === current.url) {
            // Tried once on a fresh lease and it failed again: the object is gone, not
            // the signature. Stop asking and let the glyph stand.
            publish(workTrialId, { ...current, url: '', phase: 'error', mintedAt: Date.now() });
            return;
        }
        failed.current = current.url;
        // Bumped before the read, so the element is replaced whether or not the new URL
        // differs. A `src` React does not change is a `src` the browser never re-requests.
        publish(workTrialId, { ...current, attempt: current.attempt + 1 });
        mint(workTrialId);
    }, [workTrialId, active]);

    return { ...snapshot, reload, onImageError };
};
